/// <amd-module name="Feed/Legacy/ControllerViews/RecycleBinControllerView" />
import * as React from "react";

import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import { CheckboxVisibility, ColumnActionsMode, ConstrainMode, DetailsListLayoutMode, IColumn } from "OfficeFabric/DetailsList";

import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";

import { Hub } from "VSSUI/Components/Hub/Hub";
import { IHubBreadcrumbItem } from "VSSUI/Components/HubHeader/HubBreadcrumb.Props";
import { HubHeader } from "VSSUI/Components/HubHeader/HubHeader";
import { Dialog } from "VSSUI/Dialog";
import { FilterBar } from "VSSUI/FilterBar";
import { IPivotBarAction, PivotBarItem } from "VSSUI/PivotBar";
import { Spinner } from "VSSUI/Spinner";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { css } from "VSSUI/Util";
import { FILTER_CHANGE_EVENT, IFilter, IFilterState } from "VSSUI/Utilities/Filter";
import { VssDetailsList, VssDetailsListRowStyle } from "VSSUI/VssDetailsList";
import { IVssIconProps, VssIconType } from "VSSUI/VssIcon";

import { HubViewDefaultPivots, PackageCommandIds, RecycleBinFilterBarConstants } from "Feed/Common/Constants/Constants";
import { Package } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { MessageState } from "Feed/Common/Types/IFeedMessage";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { RecycleBinActions } from "Feed/Legacy/Actions/RecycleBinActions";
import { DescriptionGridCell } from "Feed/Legacy/Components/DescriptionGridCell";
import { LoadingContainer } from "Feed/Legacy/Components/LoadingContainer";
import { NoResultsPane } from "Feed/Legacy/Components/NoResultsPane";
import { PackageNameGridCell } from "Feed/Legacy/Components/PackageNameGridCell";
import { PackageGridMessages } from "Feed/Legacy/Helpers/PackageGridMessages";
import * as RecycleBinDisplayProps from "Feed/Legacy/Helpers/RecycleBinDisplayProps";
import { getPackageDetailsPageUrl } from "Feed/Legacy/Helpers/UrlHelper";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { FeedStore } from "Feed/Legacy/Stores/FeedStore";
import { FeedState } from "Feed/Legacy/Types/FeedState";

import { ILegacyControllerViewProps } from "./ILegacyControllerViewProps";

interface IRecycleBinControllerViewProps {
    store: FeedStore;
}

class RecycleBinControllerView extends VssComponent<IRecycleBinControllerViewProps, FeedState> {
    private _filter: IFilter;
    private _isOnPrem: boolean;
    private _isEmptyRecycleBinEnabled: boolean;

    constructor(props: IRecycleBinControllerViewProps, context: IVssComponentContext) {
        super(props, context);
        this.state = this.getState();
        this._filter = this.state.recycleBinState.hubViewState.filter;

        this._isEmptyRecycleBinEnabled = !isFeatureFlagEnabled(
            context.pageContext,
            "Packaging.Feed.DisableEmptyRecycleBin"
        );

        const pageData = this.context.pageContext.getService<IVssPageService>("IVssPageService").getData();
        this._isOnPrem = pageData.isHosted === false;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.props.store.addChangedListener(this.triggerRender);
        this._filter.subscribe(this._onFilterStateChangeHandler, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount(): void {
        this.props.store.removeChangedListener(this.triggerRender);
        this._filter.unsubscribe(this._onFilterStateChangeHandler, FILTER_CHANGE_EVENT);
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        const packagesToDisplay: Package[] = RecycleBinDisplayProps.getAllNonDeletedPackageVersions(
            this.state.recycleBinState.recycleBinPackages
        );

        const footerButtonProps = [
            {
                onClick: this.closeDialog,
                text: PackageResources.Dialog_CloseButtonText
            },
            {
                children: (
                    <div>
                        {this.state.recycleBinState.dialogProcessing && <span className="bowtie-icon bowtie-spinner" />}
                        <span>
                            {this.state.recycleBinState.dialogProcessing
                                ? PackageResources.EmptyingRecycleBinButtonText
                                : PackageResources.EmptyRecycleBinButtonText}
                        </span>
                    </div>
                ),
                className: css("save-button", "delete-button"),
                disabled: this.state.recycleBinState.dialogProcessing,
                onClick: this.emptyRecycleBin,
                primary: true
            }
        ];

        return (
            <div className="packages-controllerview recycle-bin-controller-view relative">
                {/* hidden h1 for accessibility */}
                <h1 className="visibly-hidden">{PackageResources.RecycleBin_Breadcrumb}</h1>
                <Hub
                    className="packages-hubcontent absolute-fill"
                    hubViewState={this.state.recycleBinState.hubViewState}
                    hideFullScreenToggle={true}
                    commands={this._getMainCommands(packagesToDisplay)}
                >
                    <HubHeader breadcrumbItems={this._getHeaderBreadcrumbItems()} />
                    <FilterBar>
                        <KeywordFilterBarItem filterItemKey={RecycleBinFilterBarConstants.KeywordFilterKey} />
                    </FilterBar>
                    <PivotBarItem
                        className="packages-pivotBarItem"
                        itemKey={HubViewDefaultPivots.recycleBin}
                        name="" // this is the only pivot, so it won't be displayed.
                        commands={[]}
                    >
                        <LoadingContainer isLoading={this.state.packagesLoading}>
                            {packagesToDisplay && packagesToDisplay.length > 0 ? (
                                <div aria-label={PackageResources.AriaLabel_PackageList}>
                                    <VssDetailsList
                                        actionsColumnKey="name"
                                        rowStyle={VssDetailsListRowStyle.twoLine}
                                        allocateSpaceForActionsButtonWhileHidden={false}
                                        getMenuItems={this._getContextMenuCommands}
                                        shouldDisplayActions={this._shouldDisplayContextMenuCommands}
                                        ariaLabel={PackageResources.PackageListAriaLabel}
                                        ariaLabelForListHeader={PackageResources.PackageHeadersAriaLabel}
                                        ariaLabelForSelectionColumn={
                                            PackageResources.DetailsList_SelectionColumn_AriaLabel
                                        }
                                        checkButtonAriaLabel={PackageResources.DetailsList_Select_AriaLabel}
                                        ariaLabelForSelectAllCheckbox={PackageResources.DetailsList_SelectAll_AriaLabel}
                                        getRowAriaLabel={this._getRowAriaLabel}
                                        items={packagesToDisplay}
                                        columns={this._getGridColumns()}
                                        constrainMode={ConstrainMode.unconstrained}
                                        checkboxVisibility={CheckboxVisibility.hidden}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        onRowDidMount={this._loadNextPage}
                                    />
                                    <div>{this.state.nextPageLoading ? <Spinner /> : null}</div>
                                </div>
                            ) : (
                                    this._getNoPackagesMessage()
                                )}
                        </LoadingContainer>
                    </PivotBarItem>
                    {this.state.recycleBinState.dialogOpen && (
                        <Dialog
                            calloutClassName="empty-recycle-bin-dialog"
                            footerButtonProps={footerButtonProps}
                            lightDismiss={false}
                            onDismiss={this.closeDialog}
                            titleProps={{ text: PackageResources.EmptyRecycleBinDialog_HeaderText }}
                        >
                            <div>{PackageResources.EmptyRecycleBinDialog_ConfirmText}</div>
                            <br />
                            <br />
                            <div>{this._getFooterText()}</div>
                        </Dialog>
                    )}
                </Hub>
            </div>
        );
    }

    private closeDialog = (): void => {
        RecycleBinActions.EmptyDialogClosed.invoke({});
    };

    private emptyRecycleBin = (): void => {
        RecycleBinActions.EmptyRecycleBin.invoke({});
    };

    private _getFooterText(): string {
        if (this._isOnPrem) {
            return PackageResources.OnPremDeleteDelayWarning;
        }

        return "";
    }

    private _getHeaderBreadcrumbItems(): IHubBreadcrumbItem[] {
        const state = this.state;
        const items: IHubBreadcrumbItem[] = [];

        if (state.selectedFeed) {
            items.push({
                key: "feed",
                text: state.selectedFeed.name,
                onClick: () => Actions.FeedBreadcrumbSelected.invoke(null)
            });

            items.push({
                key: "recycleBin",
                text: PackageResources.RecycleBin_Breadcrumb
            });
        }

        return items;
    }

    private _getMainCommands(packagesToDisplay: Package[]): IPivotBarAction[] {
        const commands: IPivotBarAction[] = [];
        if (this._isEmptyRecycleBinEnabled) {
            const recycleBinAction: IPivotBarAction = {
                key: PackageCommandIds.EmptyRecycleBin,
                name: PackageResources.EmptyRecycleBinButtonText,
                important: true,
                iconProps: {
                    iconType: VssIconType.bowtie,
                    iconName: "bowtie-recycle-bin"
                } as IVssIconProps,
                onClick: () => RecycleBinActions.EmptyDialogOpened.invoke({}),
                disabled: packagesToDisplay.length < 1
            } as IPivotBarAction;
            commands.push(recycleBinAction);
        }
        return commands;
    }

    private _getGridColumns(): IColumn[] {
        return [
            {
                key: "name",
                fieldName: "name",
                name: PackageResources.PackageNameColumn,
                minWidth: 200,
                maxWidth: 450,
                isResizable: true,
                headerClassName: "package-name-header package-header",
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: Package, index: number, column: IColumn) => {
                    const protocol: IPackageProtocol = this.state.protocolMap[item.protocolType];
                    return (
                        <PackageNameGridCell
                            feedName={this.state.selectedFeed.name}
                            pkg={item}
                            protocol={protocol}
                            actions={{ PackageSelected: RecycleBinActions.PackageSelected }}
                            packageHref={getPackageDetailsPageUrl(
                                this.context.pageContext,
                                this.state.selectedFeed.name,
                                item,
                                item.versions[0].version,
                                true
                            )}
                            isRecycleBin={true}
                        />
                    );
                }
            } as IColumn,
            {
                key: "description",
                fieldName: "description",
                name: PackageResources.DescriptionColumn,
                minWidth: 200,
                maxWidth: 500,
                isResizable: true,
                headerClassName: "package-header",
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: Package, index: number, column: IColumn) => {
                    return <DescriptionGridCell item={item} />;
                }
            } as IColumn
        ];
    }

    private _onFilterStateChangeHandler = (filterState: IFilterState, action: string): void => {
        const packageFilterNameValue = this._filter.getFilterItemValue<string>(
            RecycleBinFilterBarConstants.KeywordFilterKey
        );

        RecycleBinActions.FilterPackages.invoke(packageFilterNameValue);
    };

    private _shouldDisplayContextMenuCommands = (item: Package): boolean => {
        return false;
    };

    private _getContextMenuCommands = (item: Package): IContextualMenuItem[] => {
        return [];
    };

    private _getRowAriaLabel = (item: Package): string => {
        return item && item.versions && item.versions.length > 0 ? item.name + " " + item.versions[0].version : null;
    };

    private _loadNextPage = (item: Package, index: number): void => {
        const rowNumber = index + 1;
        const isLastRow = rowNumber === this.state.recycleBinState.recycleBinPackages.length;
        const onPageBoundary = rowNumber % this.state.pageSize === 0;

        if (isLastRow && onPageBoundary) {
            RecycleBinActions.NextPackagePageRequested.invoke({});
        }
    };

    private _getNoPackagesMessage(): React.ReactNode {
        const filterText = this._filter.getFilterItemValue<string>(RecycleBinFilterBarConstants.KeywordFilterKey);
        const messageState = PackageGridMessages.getMessageForRecyclebin(
            this.state.selectedFeed,
            this.state.recycleBinState.recycleBinPackages,
            filterText
        );
        if (messageState === MessageState.EmptyFilter) {
            return (
                <NoResultsPane
                    pageContext={this.context.pageContext}
                    header={PackageResources.PackageGrid_NoPackagesMessage}
                    subheader={""}
                    link={""}
                    linkText={""}
                    iconClass={"bowtie-search"}
                />
            );
        }

        return (
            <NoResultsPane
                pageContext={this.context.pageContext}
                header={PackageResources.RecycleBin_NoPackagesHeader}
                subheader={PackageResources.RecycleBin_NoPackagesSubheader}
                link={""}
                linkText={""}
                iconClass={"bowtie-recycle"}
            />
        );
    }

    public getStore(): FeedStore {
        return this.props.store;
    }

    public getState(): FeedState {
        return this.getStore().getFeedState();
    }

    private triggerRender = () => this.setState(this.getState());
}

function LegacyRecyleBinControllerView(props: ILegacyControllerViewProps) {
    return <RecycleBinControllerView store={props.feedFluxStore} />;
}

VssComponent.register("recyclebin", LegacyRecyleBinControllerView);
